import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Camera,
  Check,
  CheckCircle2,
  ChevronRight,
  Gauge,
  Images,
  MapPin,
  MessageSquarePlus,
  Truck,
  X,
} from 'lucide-react';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  Field,
  FuelGauge,
  Grid,
  IconTile,
  Inline,
  Input,
  ProgressBar,
  SignaturePad,
  Stack,
  Text,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
} from '@relay-towing/ui';
import {
  ChecklistItem,
  InspectionSubjectType,
  TruckOption,
} from '@/api/truckInspections';
import { REPORT_PHOTO_ITEM, useInspectionRun } from '@/hooks/useInspectionRun';
import { useDriverStateV2 } from '@/hooks/useDriverStateV2';
import { isEmbeddedMode } from '@/utils/embeddedMode';
import { useToast } from '@/contexts/ToastContext';
import { truckName, whenLabel } from '@/lib/inspections';
import { LastCheckCard } from './components/LastCheckCard';
import {
  RowListSkeleton,
  RunScreenSkeleton,
} from './components/InspectionSkeletons';
import {
  fuelLevelsFor,
  isOdometer,
  odometerDigits,
  odometerUnit,
  withCommas,
} from './readings';

/** The card every question and every panel on this screen sits in. */
const CARD = 'rounded-xl border border-border bg-card p-4';

/** The rounded card the truck list sits in, the same one the home screen uses. */
const LIST =
  'divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const todayLabel = () =>
  new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

/**
 * A thumbnail straight from the blob, since there is no URL yet. The object
 * URL is handed back to the browser when the thumbnail goes.
 */
function PendingThumb({
  blob,
  onRemove,
}: {
  blob: Blob;
  onRemove: () => void;
}) {
  const src = useMemo(() => URL.createObjectURL(blob), [blob]);
  useEffect(() => () => URL.revokeObjectURL(src), [src]);
  return (
    <div className="relative">
      <Stack gap={1} align="center">
        <img
          src={src}
          alt=""
          className="h-16 w-16 rounded-lg object-cover opacity-70"
        />
        <Text variant="caption" tone="muted">
          Pending
        </Text>
      </Stack>
      <Button
        variant="secondary"
        size="icon"
        aria-label="Remove photo"
        onClick={onRemove}
        className="absolute -right-1.5 -top-1.5 h-5 w-5 rounded-full"
      >
        <X />
      </Button>
    </div>
  );
}

/**
 * One checklist, run on one truck or one piece of equipment. Built to take two
 * minutes.
 *
 * The old shell's run screen. What it does lives in useInspectionRun, which
 * the new shell's run screen (src/mobile/inspections) shares; this file only
 * draws it, and goes with the old shell.
 */
export default function InspectionRunScreen() {
  const { templateId = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { driver, location, locationError, isLocationLoading, actions } =
    useDriverStateV2();

  const driverFullName = driver
    ? [driver.firstName, driver.lastName].filter(Boolean).join(' ')
    : '';

  const {
    checklists,
    row,
    covers,
    kind,
    setSubjectKind,
    equipmentLabel,
    setEquipmentLabel,
    trucks,
    starting,
    data,
    template,
    items,
    stage,
    setStage,
    startOn,
    unavailable,
    draftFor,
    setDraft,
    pendingFor,
    pendingCount,
    reportPhotoUrls,
    reportPhotoCount,
    addFiles,
    removePhoto,
    removePending,
    notes,
    setNotes,
    signedName,
    setSignedName,
    setSignature,
    online,
    required,
    doneCount,
    remaining,
    progress,
    failedItems,
    statusLine,
    notSigned,
    firstBlock,
    hasWork,
    changeSubject: clearAndChangeSubject,
    finish,
    sending,
    filed,
  } = useInspectionRun({
    templateId,
    signerName: driverFullName,
    notify: (message) => toast.showToast('error', message),
  });
  /** The odometer being typed into, shown without its commas. */
  const [editingOdometer, setEditingOdometer] = useState<string | null>(null);

  const [changing, setChanging] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  // The photo strip the choose dialog was opened from, or null when it is
  // shut. One dialog serves every question and the report photos.
  const [choosingFor, setChoosingFor] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  // A checklist that is not on this driver's list cannot be run; the hook
  // says when, and this screen sends them back to the list.
  useEffect(() => {
    if (unavailable === 'error') {
      toast.showToast('error', 'Could not load the checklist');
      navigate('/inspections');
    } else if (unavailable === 'missing') {
      toast.showToast('error', 'That checklist is not available');
      navigate('/inspections');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unavailable]);

  // A new checklist opens with every question shut and nothing asked.
  useEffect(() => {
    setExpanded(null);
    setChanging(false);
  }, [templateId]);

  const requestLocation = actions.requestLocation;

  // Ask on open only where asking costs the driver nothing: inside the phone
  // app, where the shell already holds the permission, and in a browser that
  // has already been given it. A first-time prompt waits for the tap, so it
  // arrives attached to something the driver did.
  useEffect(() => {
    let dropped = false;
    // isEmbeddedMode, not the raw query string. The shells pass ?embedded=true
    // once on the very first load; by the time a driver reaches this screen the
    // router has replaced the URL, so reading it here always said no and the
    // native location bridge was never asked.
    const embedded = isEmbeddedMode();
    if (embedded) {
      requestLocation();
      return;
    }
    navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((permission) => {
        if (!dropped && permission.state === 'granted') requestLocation();
      })
      .catch(() => {
        // Safari before 16 has no permissions API. The tap still works.
      });
    return () => {
      dropped = true;
    };
  }, [requestLocation]);

  /**
   * Opens the camera or the roll for one photo strip.
   *
   * The input is clicked first and the dialog closed after, so the click
   * happens inside the tap the browser is already treating as a user gesture.
   * Closing first puts a render in between, and a file input opened outside a
   * gesture is one a browser is entitled to ignore.
   */
  const openPicker = (itemId: string, how: 'camera' | 'roll') => {
    fileInputs.current[`${itemId}:${how}`]?.click();
    setChoosingFor(null);
  };

  const changeSubject = () => {
    clearAndChangeSubject();
    setChanging(false);
    setExpanded(null);
  };

  // A blank form goes straight back. One with answers on it asks first.
  const askToChangeSubject = () => {
    if (hasWork()) {
      setChanging(true);
      return;
    }
    changeSubject();
  };

  /** Says what still stands in the way, and opens the question it is on. */
  const blocked = () => {
    const block = firstBlock();
    if (!block) return false;
    toast.showToast('error', block.message);
    if (block.itemId) setExpanded(block.itemId);
    return true;
  };

  const goToSign = () => {
    if (blocked()) return;
    setStage('sign');
  };

  const askToSubmit = () => {
    if (blocked()) return;
    setConfirming(true);
  };

  const send = async () => {
    if (await finish(location)) setConfirming(false);
  };

  /**
   * The last word before anything is sent. Both ways out of the run end here:
   * the checklist's own submit button for a template with no signature, and
   * the send button on the signing screen. It used to hang off the checklist
   * alone, so signing and sending went straight through with nothing but a
   * spinner to show for it.
   */
  const changeDialog = (
    <Dialog open={changing} onOpenChange={setChanging}>
      <DialogContent
        size="sm"
        hideCloseButton
        title="Start over on something else?"
        description="The answers on this one are cleared. Photos you have taken go with them."
        className="inset-x-6 bottom-auto top-1/2 -translate-y-1/2 rounded-xl sm:max-w-[340px]"
        footer={
          <>
            <DialogClose asChild>
              <Button variant="outline">Keep answering</Button>
            </DialogClose>
            <Button onClick={changeSubject}>Start over</Button>
          </>
        }
      />
    </Dialog>
  );

  /**
   * Camera or camera roll, asked once and answered with a tap.
   *
   * A driver at the truck wants the camera. One filing for a job they finished
   * an hour ago wants the shot already on the phone. Which they picked is what
   * the report says.
   */
  const photoSourceDialog = (
    <Dialog
      open={choosingFor !== null}
      onOpenChange={(open) => {
        if (!open) setChoosingFor(null);
      }}
    >
      <DialogContent
        size="sm"
        title="Add a photo"
        description="Take one now, or pick one already on this phone."
        className="inset-x-6 bottom-auto top-1/2 -translate-y-1/2 rounded-xl sm:max-w-[340px]"
      >
        <Stack gap={2}>
          <Button
            size="xl"
            fullWidth
            onClick={() => choosingFor && openPicker(choosingFor, 'camera')}
          >
            <Camera />
            Take a photo
          </Button>
          <Button
            variant="outline"
            size="xl"
            fullWidth
            onClick={() => choosingFor && openPicker(choosingFor, 'roll')}
          >
            <Images />
            Choose from photos
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );

  const confirmDialog = (
    <Dialog open={confirming} onOpenChange={setConfirming}>
      <DialogContent
        size="sm"
        hideCloseButton
        title="Submit inspection?"
        description={
          failedItems.length > 0
            ? `You flagged ${plural(
                failedItems.length,
                'issue'
              )}. This goes to the shop and can't be edited after submitting.`
            : "Are you sure you want to submit this inspection? It can't be edited after submitting."
        }
        /*
          Centred on a phone too, rather than the library's bottom sheet: this
          is a two-line decision, and a sheet that covers the checklist behind
          it reads as a bigger moment than it is. The inset sets the width
          without a bare `w-` class, which the library rightly warns about —
          an unprefixed width is thrown away again from `sm` up.
        */
        className="inset-x-6 bottom-auto top-1/2 -translate-y-1/2 rounded-xl sm:max-w-[340px]"
        footer={
          <>
            <DialogClose asChild>
              <Button variant="outline" disabled={sending}>
                Cancel
              </Button>
            </DialogClose>
            <Button onClick={send} loading={sending} disabled={sending}>
              Submit
            </Button>
          </>
        }
      />
    </Dialog>
  );

  // ---------------------------------------------------------------- the done

  if (filed) {
    return (
      <Stack
        gap={0}
        align="center"
        justify="center"
        className="min-h-screen bg-background px-6 py-12 text-center"
      >
        <IconTile
          icon={CheckCircle2}
          size={16}
          shape="circle"
          tone="success"
          className="text-success-subtle-foreground"
        />

        <Text as="h1" variant="h2" className="mt-5">
          Inspection submitted
        </Text>
        <Text variant="body-sm" tone="muted" className="mt-2 text-pretty">
          {data?.subject.label ?? ''}{' '}
          {filed.flagged === 0 ? 'passed.' : 'is reported with issues.'}
        </Text>

        <Stack gap={2} className={`mt-6 w-full text-left ${CARD}`}>
          <Inline justify="between" align="center" gap={3}>
            <Text variant="body-sm" tone="muted">
              Items passed
            </Text>
            <Text variant="body-sm-strong" numeric>
              {filed.passed}
            </Text>
          </Inline>
          <Inline justify="between" align="center" gap={3}>
            <Text variant="body-sm" tone="muted">
              Issues flagged
            </Text>
            <Text
              variant="body-sm-strong"
              tone={filed.flagged > 0 ? 'destructive' : 'default'}
              numeric
            >
              {filed.flagged}
            </Text>
          </Inline>
          {filed.id && (
            <Inline justify="between" align="center" gap={3}>
              <Text variant="body-sm" tone="muted" className="shrink-0">
                Reference
              </Text>
              {/* The id is a 36 character uuid and the card is a phone wide, so
                  it gets the room that is left and an ellipsis. It is there to
                  be tapped, not read out. */}
              <Button
                variant="link"
                size="inline"
                title={filed.id}
                onClick={() => navigate(`/inspections/report/${filed.id}`)}
                className="min-w-0"
              >
                <Text
                  as="span"
                  variant="code"
                  tone="inherit"
                  truncate
                  className="min-w-0"
                >
                  {filed.id}
                </Text>
              </Button>
            </Inline>
          )}
        </Stack>

        <Button
          size="xl"
          fullWidth
          className="mt-6"
          onClick={() => navigate('/inspections')}
        >
          Back to inspections
        </Button>
      </Stack>
    );
  }

  // ----------------------------------------------------------- the subject

  // A start in flight is the form opening, not a choice still to make, so it
  // gets the skeleton rather than a second look at the list.
  if (starting) {
    return <RunScreenSkeleton onBack={() => navigate('/inspections')} />;
  }

  // Nothing can be asked until the checklist says what it covers. A checklist
  // that is not on this driver's list sends them back, above.
  if (checklists.isPending || !row) {
    return <RunScreenSkeleton onBack={() => navigate('/inspections')} />;
  }

  /*
   * What is being checked, named before a single question is drawn.
   *
   * A checklist written for trucks lists them. One written for equipment asks
   * for a name, because equipment has no id to pick from. One that covers
   * either asks which of the two first.
   */
  if (stage === 'subject') {
    const assigned = (trucks.data ?? []).filter((one) => one.isAssignedToMe);
    const rest = (trucks.data ?? []).filter((one) => !one.isAssignedToMe);
    const typed = equipmentLabel.trim();

    const startOnEquipment = () => {
      if (!typed) return;
      startOn({ equipmentLabel: typed, label: typed });
    };

    const renderTruck = (one: TruckOption) => {
      const name = truckName(one);
      // The plate only earns its place when the name is the door number.
      const plate = one.truckNumber ? one.licensePlate : null;
      const checked = one.lastCheck
        ? `Checked ${whenLabel(one.lastCheck.submittedAt)}`
        : 'Not checked yet';

      return (
        <Button
          key={one.id}
          variant="ghost"
          size="inline"
          aria-label={`Check ${name}`}
          onClick={() => startOn({ truckId: one.id, label: name })}
          className="w-full items-center justify-start gap-3 rounded-none px-4 py-3 text-left"
        >
          <IconTile
            icon={Truck}
            size={9}
            tone="neutral"
            className="text-muted-foreground"
          />
          <Stack gap={0} className="min-w-0 flex-1">
            <Text as="span" variant="body-strong" truncate className="min-w-0">
              {name}
            </Text>
            <Text as="span" variant="body-sm" tone="muted" truncate>
              {[plate, checked].filter(Boolean).join(' · ')}
            </Text>
          </Stack>
          <ChevronRight className="shrink-0 text-muted-foreground" />
        </Button>
      );
    };

    return (
      <div className="flex min-h-screen flex-col bg-background">
        <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur">
          <Inline gap={3} align="center" className="px-4 py-3">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Back to inspections"
              onClick={() => navigate('/inspections')}
            >
              <ArrowLeft />
            </Button>
            <Text as="h1" variant="body-lg-strong" truncate className="min-w-0">
              {row.name}
            </Text>
          </Inline>
        </header>

        <Stack gap={4} className="flex-1 px-4 py-4">
          <Text as="h2" variant="h3">
            {covers === 'TRUCK' ? 'Which truck?' : 'What are you checking?'}
          </Text>

          {/* Only a checklist that covers either asks. The other two already
              know what they are written for. */}
          {covers === 'ANY' && (
            <ToggleGroup
              type="single"
              value={kind}
              onValueChange={(value) => {
                if (value) setSubjectKind(value as InspectionSubjectType);
              }}
              variant="chip"
              size="lg"
              bordered={false}
              aria-label="What are you checking"
            >
              <ToggleGroupItem value="TRUCK">Truck</ToggleGroupItem>
              <ToggleGroupItem value="EQUIPMENT">Other</ToggleGroupItem>
            </ToggleGroup>
          )}

          {kind === 'TRUCK' ? (
            trucks.isPending ? (
              <RowListSkeleton label="Loading trucks" />
            ) : trucks.isError ? (
              // A list that did not arrive is not an empty yard. Saying "no
              // trucks" here is how a broken company header stays invisible.
              <Stack gap={3} align="start">
                <Text variant="body-sm" tone="muted" className="text-pretty">
                  Could not load your trucks. Check your signal and try again.
                </Text>
                <Button variant="outline" onClick={() => trucks.refetch()}>
                  Try again
                </Button>
              </Stack>
            ) : assigned.length + rest.length === 0 ? (
              <Text variant="body-sm" tone="muted" className="text-pretty">
                No trucks on file. Ask the office to add one.
              </Text>
            ) : (
              <Stack gap={4}>
                {assigned.length > 0 && (
                  <Stack gap={2}>
                    {rest.length > 0 && (
                      <Text variant="overline" tone="muted" className="px-1">
                        Assigned to you
                      </Text>
                    )}
                    <div className={LIST}>{assigned.map(renderTruck)}</div>
                  </Stack>
                )}
                {rest.length > 0 && (
                  <Stack gap={2}>
                    {assigned.length > 0 && (
                      <Text variant="overline" tone="muted" className="px-1">
                        Other trucks
                      </Text>
                    )}
                    <div className={LIST}>{rest.map(renderTruck)}</div>
                  </Stack>
                )}
              </Stack>
            )
          ) : (
            <Stack gap={3}>
              <Field label="Name it">
                <Input
                  value={equipmentLabel}
                  onChange={(event) => setEquipmentLabel(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    startOnEquipment();
                  }}
                  placeholder="e.g. Dolly 2"
                  maxLength={120}
                  aria-label="What are you checking"
                />
              </Field>
              <Button
                size="xl"
                fullWidth
                disabled={!typed}
                onClick={startOnEquipment}
              >
                Start
                <ArrowRight />
              </Button>
            </Stack>
          )}
        </Stack>
      </div>
    );
  }

  if (!template) {
    return <RunScreenSkeleton onBack={() => navigate('/inspections')} />;
  }

  const sections: [string, ChecklistItem[]][] = [];
  items.forEach((item) => {
    const key = item.section || 'Checklist';
    const existing = sections.find(([name]) => name === key);
    if (existing) existing[1].push(item);
    else sections.push([key, [item]]);
  });

  // What the server calls this run's subject: a truck in the driver's words,
  // or the name they typed for a piece of equipment.
  const name = data?.subject.label ?? '';
  // The word for it in a sentence, so the photo prompts do not tell a driver
  // holding a dolly to photograph the truck.
  const subjectWord =
    data?.subject.type === 'EQUIPMENT' ? 'equipment' : 'truck';
  // Only when the name is the door number does the plate still have something
  // to add; otherwise the name already is the plate. Equipment has neither.
  const plate = data?.truck?.truckNumber ? data.truck.licensePlate : null;
  const makeModel = [data?.truck?.make, data?.truck?.model]
    .filter(Boolean)
    .join(' ');

  // ------------------------------------------------------------- the signing

  if (stage === 'sign') {
    return (
      <div className="min-h-screen bg-background pb-28">
        <header className="sticky top-0 z-10 border-b border-border bg-card px-4 py-3">
          <Button
            variant="link"
            size="inline"
            onClick={() => setStage('checklist')}
          >
            <ArrowLeft />
            Back to the checklist
          </Button>
        </header>

        <Stack gap={6} className="p-4">
          <Stack gap={1}>
            <Text as="h1" variant="h2">
              {failedItems.length === 0
                ? 'Nothing to report'
                : `You are reporting ${failedItems.length} fault${
                    failedItems.length === 1 ? '' : 's'
                  }`}
            </Text>
            <Text variant="caption" tone="muted">
              {name} · {template.name}
            </Text>
          </Stack>

          {failedItems.length > 0 && (
            <Stack
              gap={2}
              className="rounded-lg border border-destructive/30 bg-destructive-subtle p-3 text-destructive-subtle-foreground"
            >
              {failedItems.map((item) => (
                <Inline key={item.id} gap={2} align="start">
                  <X className="size-icon-sm mt-0.5 shrink-0" />
                  <Text variant="caption-strong" className="min-w-0">
                    {item.label}
                  </Text>
                </Inline>
              ))}
            </Stack>
          )}

          {template.requireSignature && (
            <Stack gap={3}>
              <Field label="Your name">
                <Input
                  value={signedName}
                  onChange={(event) => setSignedName(event.target.value)}
                />
              </Field>
              <Field
                label="Signature"
                description="Signing says the report is true."
              >
                <SignaturePad onChange={setSignature} />
              </Field>
            </Stack>
          )}
        </Stack>

        <div className="fixed inset-x-0 bottom-0 border-t border-border bg-card p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {(statusLine || notSigned) && (
            <Text
              variant="caption"
              tone="muted"
              align="center"
              className="mb-2 block"
            >
              {statusLine ?? 'Add your name and signature to send'}
            </Text>
          )}
          <Button
            size="xl"
            fullWidth
            onClick={() => setConfirming(true)}
            loading={sending}
            disabled={sending || pendingCount > 0 || notSigned}
          >
            Send report
          </Button>
        </div>

        {confirmDialog}
      </div>
    );
  }

  // ----------------------------------------------------------- the checklist

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur">
        <Inline gap={3} align="center" className="px-4 py-3">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Back to inspections"
            onClick={() => navigate('/inspections')}
          >
            <ArrowLeft />
          </Button>
          <Text as="h1" variant="body-lg-strong">
            Inspection
          </Text>
        </Inline>
      </header>

      <Stack gap={4} className="flex-1 px-4 py-4">
        <Stack gap={3} className="rounded-2xl border border-border bg-card p-4">
          <Inline gap={3} align="start" justify="between">
            <Stack gap={0} className="min-w-0">
              <Text as="h2" variant="h3">
                {name}
              </Text>
              {makeModel && makeModel !== name && (
                <Text variant="body-sm" tone="muted">
                  {makeModel}
                </Text>
              )}
            </Stack>
            <Badge tone="neutral" className="shrink-0">
              {template.name}
            </Badge>
          </Inline>

          <Inline
            gap={3}
            wrap
            align="center"
            className="border-t border-border pt-3"
          >
            {plate && (
              <Text variant="caption" tone="muted" numeric>
                {plate}
              </Text>
            )}
            <Inline gap={1} align="center">
              <CalendarDays className="size-icon-xs text-muted-foreground" />
              <Text variant="caption" tone="muted">
                {todayLabel()}
              </Text>
            </Inline>
            {!online && (
              <Text variant="caption" tone="muted">
                Offline
              </Text>
            )}
          </Inline>
        </Stack>

        {data?.lastInspection && <LastCheckCard last={data.lastInspection} />}

        <Stack gap={2} aria-label="Inspection progress">
          <Inline gap={3} align="center" justify="between">
            <Text variant="caption-strong">
              {doneCount} of {required.length} checked
            </Text>
            <Text variant="caption" tone="muted" numeric>
              {progress}%
            </Text>
          </Inline>
          <ProgressBar
            value={progress}
            label={`${doneCount} of ${required.length} questions answered`}
          />
        </Stack>

        <Inline>
          <Button variant="ghost" size="sm" onClick={askToChangeSubject}>
            <ArrowLeft />
            Change what you&apos;re checking
          </Button>
        </Inline>

        {sections.map(([section, sectionItems]) => (
          <Stack key={section} gap={3}>
            <Text as="h3" variant="body-sm-strong" className="px-1">
              {section}
            </Text>

            {sectionItems.map((item) => {
              const draft = draftFor(item.id);
              const fuelLevels = fuelLevelsFor(item);
              const odometer = isOdometer(item);
              const isOpen = expanded === item.id;
              const failed = draft.status === 'FAIL';
              // A photo question shows its photo strip from the start, the
              // same as a question that always asks for one. It only nags
              // for the photo when the question is required.
              const showPhotos =
                item.answerType === 'PHOTO' || item.photoRule === 'ALWAYS';
              const wantsPhoto =
                item.photoRule === 'ALWAYS' ||
                (item.answerType === 'PHOTO' && item.isRequired);
              const photoCount =
                draft.photoUrls.length + pendingFor(item.id).length;
              const needsPhoto =
                (wantsPhoto || (item.photoRule === 'ON_FAIL' && failed)) &&
                photoCount === 0;

              // One quiet line rather than a row of badges: what the question
              // will and will not let the driver skip.
              const rules: string[] = [];
              if (!item.isRequired) rules.push('Optional');
              if (item.photoRule === 'ALWAYS') rules.push('Photo needed');
              else if (item.photoRule === 'ON_FAIL')
                rules.push('Photo needed if it fails');

              const boxOpen =
                failed ||
                showPhotos ||
                photoCount > 0 ||
                !!draft.note ||
                isOpen;

              return (
                <Stack key={item.id} gap={4} className={CARD}>
                  <Stack gap={1}>
                    <Text variant="body-sm-strong" className="text-pretty">
                      {item.label}
                    </Text>
                    {item.helpText && (
                      <Text variant="caption" tone="muted">
                        {item.helpText}
                      </Text>
                    )}
                    {rules.length > 0 && (
                      <Text variant="caption" tone="muted">
                        {rules.join(' · ')}
                      </Text>
                    )}
                  </Stack>

                  {item.answerType === 'PASS_FAIL' && (
                    <Grid
                      cols={2}
                      gap={2}
                      role="radiogroup"
                      aria-label={`${item.label}: pass or fail`}
                    >
                      <Button
                        variant="outline"
                        size="lg"
                        role="radio"
                        aria-checked={draft.status === 'PASS'}
                        onClick={() => {
                          setDraft(item.id, { status: 'PASS' });
                          // Fail opened this box. Passing closes it again,
                          // unless a note or a photo is already in there.
                          setExpanded((open) =>
                            open === item.id ? null : open
                          );
                        }}
                        className={
                          draft.status === 'PASS'
                            ? 'h-11 border-success bg-success-subtle text-success-subtle-foreground hover:bg-success-subtle'
                            : 'h-11'
                        }
                      >
                        <Check />
                        Pass
                      </Button>
                      <Button
                        variant="outline"
                        size="lg"
                        role="radio"
                        aria-checked={failed}
                        onClick={() => {
                          setDraft(item.id, { status: 'FAIL' });
                          setExpanded(item.id);
                        }}
                        className={
                          failed
                            ? 'h-11 border-destructive bg-destructive-subtle text-destructive-subtle-foreground hover:bg-destructive-subtle'
                            : 'h-11'
                        }
                      >
                        <X />
                        Fail
                      </Button>
                    </Grid>
                  )}

                  {item.answerType === 'YES_NO' && (
                    <Grid
                      cols={2}
                      gap={2}
                      role="radiogroup"
                      aria-label={`${item.label}: yes or no`}
                    >
                      {(['Yes', 'No'] as const).map((choice) => (
                        <Button
                          key={choice}
                          variant="outline"
                          size="lg"
                          role="radio"
                          aria-checked={draft.valueText === choice}
                          onClick={() =>
                            setDraft(item.id, { valueText: choice })
                          }
                          className={
                            draft.valueText === choice
                              ? 'h-11 border-primary bg-accent text-accent-foreground'
                              : 'h-11'
                          }
                        >
                          {choice}
                        </Button>
                      ))}
                    </Grid>
                  )}

                  {odometer && (
                    <Stack gap={2}>
                      <Input
                        size="xl"
                        inputMode="numeric"
                        autoComplete="off"
                        enterKeyHint="done"
                        // Bare digits while typing, commas once it is left.
                        // Formatting under the caret made Backspace on a
                        // comma do nothing and threw the caret to the end.
                        value={
                          editingOdometer === item.id
                            ? (draft.valueNumber ?? '')
                            : withCommas(draft.valueNumber)
                        }
                        onFocus={() => setEditingOdometer(item.id)}
                        onBlur={() => setEditingOdometer(null)}
                        onChange={(event) =>
                          setDraft(item.id, {
                            valueNumber: odometerDigits(event.target.value),
                          })
                        }
                        placeholder="0"
                        aria-label={item.label}
                        leadingIcon={<Gauge />}
                        trailingIcon={
                          <Text as="span" variant="body-sm" tone="muted">
                            {odometerUnit(item)}
                          </Text>
                        }
                      />
                      <Text variant="caption" tone="muted">
                        Enter the reading shown on the dash.
                      </Text>
                    </Stack>
                  )}

                  {item.answerType === 'NUMBER' && !odometer && (
                    <Inline gap={2} align="center">
                      <Input
                        inputMode="numeric"
                        value={draft.valueNumber ?? ''}
                        onChange={(event) =>
                          setDraft(item.id, {
                            valueNumber: event.target.value,
                          })
                        }
                        placeholder="0"
                        aria-label={item.label}
                        className="w-40"
                      />
                      {item.unit && (
                        <Text variant="caption" tone="muted">
                          {item.unit}
                        </Text>
                      )}
                    </Inline>
                  )}

                  {fuelLevels && (
                    <FuelGauge
                      levels={fuelLevels}
                      value={draft.valueText || undefined}
                      onChange={(level) =>
                        setDraft(item.id, { valueText: level })
                      }
                      label={item.label}
                    />
                  )}

                  {item.answerType === 'SELECT' && !fuelLevels && (
                    <Inline gap={2} wrap>
                      {item.options.map((option) => (
                        <Button
                          key={option}
                          variant={
                            draft.valueText === option ? 'primary' : 'outline'
                          }
                          size="sm"
                          aria-pressed={draft.valueText === option}
                          onClick={() =>
                            setDraft(item.id, { valueText: option })
                          }
                        >
                          {option}
                        </Button>
                      ))}
                    </Inline>
                  )}

                  {item.answerType === 'CHECKBOX' && (
                    <Stack gap={2}>
                      {item.options.map((option) => {
                        const ticked = draft.valueList.includes(option);
                        return (
                          <Checkbox
                            key={option}
                            checked={ticked}
                            onCheckedChange={(checked) =>
                              setDraft(item.id, {
                                valueList:
                                  checked === true
                                    ? [...draft.valueList, option]
                                    : draft.valueList.filter(
                                        (value) => value !== option
                                      ),
                              })
                            }
                            label={option}
                          />
                        );
                      })}
                    </Stack>
                  )}

                  {item.answerType === 'TEXT' && (
                    <Textarea
                      value={draft.valueText ?? ''}
                      onChange={(event) =>
                        setDraft(item.id, { valueText: event.target.value })
                      }
                      rows={2}
                      placeholder="Type here"
                      aria-label={item.label}
                    />
                  )}

                  {/*
                    A pass is not always the whole story. A headlight that
                    works but is going dim is the thing a fleet manager
                    wants weeks before it fails, and until now there was
                    nowhere to say it.
                  */}
                  {!boxOpen && (
                    <Button
                      variant="link"
                      size="inline"
                      onClick={() => setExpanded(item.id)}
                      className="self-start"
                    >
                      <MessageSquarePlus />
                      Add a note or photo
                    </Button>
                  )}

                  {boxOpen && (
                    <Stack
                      gap={3}
                      className={
                        failed
                          ? 'rounded-lg border border-destructive/30 bg-destructive-subtle p-3'
                          : 'rounded-lg border border-border bg-muted p-3'
                      }
                    >
                      <Inline gap={2} align="center">
                        {failed ? (
                          <AlertTriangle className="size-icon-xs shrink-0 text-destructive" />
                        ) : (
                          <Camera className="size-icon-xs shrink-0 text-muted-foreground" />
                        )}
                        <Text
                          variant="caption-strong"
                          tone={failed ? 'destructive' : 'muted'}
                        >
                          {failed
                            ? 'Document the issue'
                            : showPhotos
                              ? 'Add a photo'
                              : 'Add a note or photo'}
                        </Text>
                      </Inline>

                      <Textarea
                        value={draft.note ?? ''}
                        onChange={(event) =>
                          setDraft(item.id, { note: event.target.value })
                        }
                        rows={2}
                        placeholder={
                          failed
                            ? "Describe what's wrong"
                            : 'Anything worth mentioning?'
                        }
                        aria-label={`Note about ${item.label}`}
                      />

                      {photoCount === 0 ? (
                        <Button
                          variant="outline"
                          size="lg"
                          grow
                          fullWidth
                          aria-label={`Add a photo for ${item.label}`}
                          onClick={() => setChoosingFor(item.id)}
                          className={[
                            'flex-col gap-1 rounded-xl border-dashed bg-muted py-5',
                            needsPhoto ? 'border-destructive' : 'border-input',
                          ].join(' ')}
                        >
                          <Camera className="text-muted-foreground" />
                          <Text as="span" variant="body-sm-strong">
                            {failed ? 'Add photo of issue' : 'Add a photo'}
                          </Text>
                          <Text as="span" variant="caption" tone="muted">
                            Camera or camera roll
                          </Text>
                        </Button>
                      ) : (
                        <Inline gap={2} wrap align="start">
                          {draft.photoUrls.map((url) => (
                            <div key={url} className="relative">
                              <img
                                src={url}
                                alt=""
                                className="h-16 w-16 rounded-lg object-cover"
                              />
                              <Button
                                variant="secondary"
                                size="icon"
                                aria-label="Remove photo"
                                onClick={() => removePhoto(item.id, url)}
                                className="absolute -right-1.5 -top-1.5 h-5 w-5 rounded-full"
                              >
                                <X />
                              </Button>
                            </div>
                          ))}
                          {pendingFor(item.id).map((photo) => (
                            <PendingThumb
                              key={photo.localId}
                              blob={photo.blob}
                              onRemove={() =>
                                removePending(item.id, photo.localId)
                              }
                            />
                          ))}
                          <Button
                            variant="outline"
                            aria-label={`Add another photo for ${item.label}`}
                            onClick={() => setChoosingFor(item.id)}
                            className="h-16 w-16 border-dashed border-input"
                          >
                            <Camera />
                          </Button>
                        </Inline>
                      )}

                      {/* Two hidden inputs, one per way in, opened by the
                          choose dialog. `capture` is the only way to ask for
                          the camera over the gallery and the library has no
                          equivalent, so they are raw inputs. */}
                      {/* eslint-disable-next-line no-restricted-syntax -- design-tokens-ignore: a hidden file input the camera and camera-roll buttons open; it draws nothing */}
                      <input
                        ref={(element) => {
                          fileInputs.current[`${item.id}:camera`] = element;
                        }}
                        type="file"
                        accept="image/*"
                        multiple
                        capture="environment"
                        onChange={(event) => {
                          void addFiles(item.id, event.target.files, 'CAMERA');
                          event.target.value = '';
                        }}
                        className="hidden"
                      />
                      {/* eslint-disable-next-line no-restricted-syntax -- design-tokens-ignore: a hidden file input the camera and camera-roll buttons open; it draws nothing */}
                      <input
                        ref={(element) => {
                          fileInputs.current[`${item.id}:roll`] = element;
                        }}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(event) => {
                          void addFiles(item.id, event.target.files, 'UPLOAD');
                          event.target.value = '';
                        }}
                        className="hidden"
                      />

                      {needsPhoto && (
                        <Text variant="caption-strong" tone="destructive">
                          {failed
                            ? 'Photograph what is wrong before you send this'
                            : 'Add a photo before you send this'}
                        </Text>
                      )}
                    </Stack>
                  )}
                </Stack>
              );
            })}
          </Stack>
        ))}

        <Field
          label={
            <>
              Note{' '}
              <Text as="span" variant="inherit" tone="muted" weight="normal">
                (optional)
              </Text>
            </>
          }
        >
          <Textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={3}
            placeholder="Anything else the shop should know?"
          />
        </Field>

        {/*
          A photo of the thing being checked rather than of any one question.
          The report has carried these all along; the phone had no way to add
          one.
        */}
        <Field
          label={
            <>
              Photo{' '}
              <Text as="span" variant="inherit" tone="muted" weight="normal">
                (optional)
              </Text>
            </>
          }
        >
          {reportPhotoCount === 0 ? (
            <Button
              variant="outline"
              size="lg"
              grow
              fullWidth
              aria-label={`Add a photo of the ${subjectWord}`}
              onClick={() => setChoosingFor(REPORT_PHOTO_ITEM)}
              className="flex-col gap-1 rounded-xl border-dashed border-input bg-muted py-5"
            >
              <Camera className="text-muted-foreground" />
              <Text as="span" variant="body-sm-strong">
                Add a photo of the {subjectWord}
              </Text>
              <Text as="span" variant="caption" tone="muted">
                Camera or camera roll
              </Text>
            </Button>
          ) : (
            <Inline gap={2} wrap align="start">
              {reportPhotoUrls.map((url) => (
                <div key={url} className="relative">
                  <img
                    src={url}
                    alt=""
                    className="h-16 w-16 rounded-lg object-cover"
                  />
                  <Button
                    variant="secondary"
                    size="icon"
                    aria-label="Remove photo"
                    onClick={() => removePhoto(REPORT_PHOTO_ITEM, url)}
                    className="absolute -right-1.5 -top-1.5 h-5 w-5 rounded-full"
                  >
                    <X />
                  </Button>
                </div>
              ))}
              {pendingFor(REPORT_PHOTO_ITEM).map((photo) => (
                <PendingThumb
                  key={photo.localId}
                  blob={photo.blob}
                  onRemove={() =>
                    removePending(REPORT_PHOTO_ITEM, photo.localId)
                  }
                />
              ))}
              <Button
                variant="outline"
                aria-label={`Add another photo of the ${subjectWord}`}
                onClick={() => setChoosingFor(REPORT_PHOTO_ITEM)}
                className="h-16 w-16 border-dashed border-input"
              >
                <Camera />
              </Button>
            </Inline>
          )}

          {/* The same pair the questions use. */}
          {/* eslint-disable-next-line no-restricted-syntax -- design-tokens-ignore: a hidden file input the camera and camera-roll buttons open; it draws nothing */}
          <input
            ref={(element) => {
              fileInputs.current[`${REPORT_PHOTO_ITEM}:camera`] = element;
            }}
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            onChange={(event) => {
              void addFiles(REPORT_PHOTO_ITEM, event.target.files, 'CAMERA');
              event.target.value = '';
            }}
            className="hidden"
          />
          {/* eslint-disable-next-line no-restricted-syntax -- design-tokens-ignore: a hidden file input the camera and camera-roll buttons open; it draws nothing */}
          <input
            ref={(element) => {
              fileInputs.current[`${REPORT_PHOTO_ITEM}:roll`] = element;
            }}
            type="file"
            accept="image/*"
            multiple
            onChange={(event) => {
              void addFiles(REPORT_PHOTO_ITEM, event.target.files, 'UPLOAD');
              event.target.value = '';
            }}
            className="hidden"
          />
        </Field>

        <Field
          label="Where you are"
          description="Goes on the report, so the office knows where this was done."
        >
          {location ? (
            <Inline gap={3} align="center" wrap>
              <Inline gap={2} align="center" className="min-w-0">
                <MapPin className="size-icon-sm shrink-0 text-success" />
                <Stack gap={0} className="min-w-0">
                  <Text variant="caption-strong">Pinned</Text>
                  <Text variant="caption" tone="muted" numeric>
                    {location.latitude.toFixed(5)},{' '}
                    {location.longitude.toFixed(5)}
                  </Text>
                </Stack>
              </Inline>
              <Button
                variant="ghost"
                size="sm"
                onClick={requestLocation}
                loading={isLocationLoading}
              >
                Pin again
              </Button>
            </Inline>
          ) : (
            <Stack gap={2}>
              <Button
                variant="outline"
                fullWidth
                onClick={requestLocation}
                loading={isLocationLoading}
              >
                <MapPin />
                Pin where you are
              </Button>
              {locationError && (
                <Text variant="caption" tone="muted">
                  Your phone did not share a location. Turn location on for this
                  app, or send the report without it.
                </Text>
              )}
            </Stack>
          )}
        </Field>
      </Stack>

      <div className="pointer-events-none sticky bottom-0 z-10 mt-auto px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2">
        <Stack
          gap={2}
          className="pointer-events-auto rounded-2xl border border-border bg-card/95 p-2 shadow-e2 backdrop-blur"
        >
          {statusLine && (
            <Text
              variant="caption"
              tone="muted"
              align="center"
              className="pt-1"
            >
              {statusLine}
            </Text>
          )}
          <Button
            size="xl"
            fullWidth
            disabled={remaining > 0}
            onClick={template.requireSignature ? goToSign : askToSubmit}
          >
            {remaining > 0 ? (
              `Answer all ${required.length} items to submit`
            ) : template.requireSignature ? (
              'Continue to sign'
            ) : (
              <>
                Submit inspection
                <ArrowRight />
              </>
            )}
          </Button>
        </Stack>
      </div>

      {confirmDialog}
      {changeDialog}
      {photoSourceDialog}
    </div>
  );
}
